/**
 * @fileOverview The file browser's walkthrough. Pure data.
 * See docs/view-help.md and docs/view-help/files.md.
 */

import { PERMISSIONS } from "@/services/permission"
import { stepsOn } from "@/services/viewHelp/define"
import type { ViewHelpTour } from "@/services/viewHelp/types"

/**
 * The one folder the walkthrough walks into, and the file it opens there.
 *
 * Both live in the browser's **query**, not in its path (`views/file/FileBrowserOverview.vue` reads
 * `?path=`), so `at.query` is what says where a step is read. It is only the fallback: the frame
 * gets there by the pointer clicking the place and then the row, which is what the steps are about.
 */
const NORD = { query: { path: "PROJECT/p-nord" } }

/** Musterprojekt Nord's exemption certificate — the one file that is not a PDF. See "Decisions". */
const DETAILS_ROW = "files-row-fil-nord-freistellung"

/**
 * The "Gültig bis" cell of its building permit, whose date is inside its warning lead — so the step
 * about the column lands on a chip that is actually saying something, with the expired one below it.
 */
const DUE_CELL = "files-due-fil-nord-genehmigung"

const onBrowser = stepsOn("FileBrowser", { navEntry: "nav-file" })
const inFolder = stepsOn("FileBrowser", { navEntry: "nav-file", at: NORD })

// The related views: where the files come from, and what the due date is worth. The pointer walks
// there (`services/viewHelp/ways.ts`); `at` says where to load the frame if that walk ever fails.
const onProject = stepsOn("ProjectDetail", {
  permissions: [PERMISSIONS.VIEW_ASSIGNED_PROJECT_FILE, PERMISSIONS.VIEW_FOREIGN_PROJECT_FILE],
  at: { params: { projectId: "p-nord" }, hash: "files" },
})
const onSettings = stepsOn("SettingsOverview", { at: { hash: "notification" } })
// The same page, its Berechtigungen register: the rights of a role decide which places the browser
// has at all. The step presses the register itself, since the way ends on Benachrichtigungen.
const onRights = stepsOn("SettingsOverview", { at: { hash: "rights" } })

/**
 * Dateien: every attachment in the workspace in one tree, whichever record it hangs off.
 *
 * The browser owns no documents of its own — it is a second way into the eight Dateiablagen the
 * product already has. So the walkthrough spends its own steps on **finding** a document (the places,
 * the search, the path, the due column) and its Zusammenhänge chapter on **where a document comes
 * from**, **what a due date makes happen** and **what decides which places there are**.
 *
 * Nothing is uploaded, downloaded or dragged. The upload button is pointed at and explained, the
 * preview is named rather than opened (the sandbox refuses a file, so it would open on nothing), and
 * the one dialog the pointer opens is left by "Abbrechen".
 */
export const FILES_TOUR: ViewHelpTour = {
  key: "files",
  // The drawer's own glyph for Dateien (`services/navigation.js`).
  icon: "mdi-folder-multiple-outline",
  routes: ["FileBrowser"],
  // Where a file is put in, where its due date is turned into a notification, and where the rights
  // that decide which places a person sees are kept.
  related: ["ProjectDetail", "SettingsOverview"],
  scenes: [
    // A term typed into the search, undone with the field's ✕: a result list left on screen would
    // hide the places the next steps walk into.
    { key: "search", enter: [], exit: [{ do: "clear", target: "files-search" }] },
    // Walking down into one folder, and the file dialog opened at the end of it. One scene, because
    // every step after the first is read *inside* that folder: with a scene each, a jump onto a later
    // step would find neither the folder nor the row it points at. As one, the player replays the two
    // clicks at speed first, from wherever the reader jumped in from — the place is clicked in the
    // pane on the left, which is on the screen at every path.
    // The exit closes the dialog by pressing Abbrechen. The folder itself is left open on purpose:
    // the steps that follow are the drawer and the trigger, and neither cares where the browser
    // stands.
    { key: "browse", enter: [], exit: [{ do: "click", target: "modal-dialog-cancel" }] },
    // The project's Dateiablage, reached by the register bar of its page.
    { key: "project-files", enter: [], exit: [] },
    // The settings' Berechtigungen register, pressed by the step that explains it. Nothing to undo:
    // the register is the page's own state and the walkthrough ends here.
    { key: "settings-rights", enter: [], exit: [] },
  ],
  steps: [
    // ── Dateien ──────────────────────────────────────────────────────────────
    // What the page is for, before any of its parts: the whole browser, with no zoom to go in on.
    onBrowser("intro", "files-browser", "mdi-folder-search-outline", { placement: "top", zoom: false }),
    onBrowser("places", "files-places", "mdi-folder-multiple-outline", { placement: "right" }),
    // No zoom: the point is the list beside the field turning into what matches.
    onBrowser("search", "files-search", "mdi-magnify", {
      placement: "bottom",
      scene: "search",
      zoom: false,
      actions: [{ do: "type", target: "files-search", text: "Bau" }],
    }),
    // Pointed at, not driven. It is a select without a ✕, so a scene could only take it back by
    // opening its menu again beside the next step — which is the one thing an exit must not do.
    onBrowser("due-filter", "files-due", "mdi-calendar-alert", { placement: "bottom" }),
    onBrowser("storage", "files-storage", "mdi-harddisk", { placement: "bottom" }),

    // ── one folder, opened ───────────────────────────────────────────────────
    // The camera keeps to the whole page while the pointer clicks: zoomed onto the place it presses,
    // the list that changes underneath would be out of the picture.
    onBrowser("place", "files-list", "mdi-briefcase-outline", {
      placement: "top",
      scene: "browse",
      zoom: false,
      actions: [{ do: "click", target: "files-place-PROJECT" }],
    }),
    inFolder("record", ["files-path", "files-summary"], "mdi-folder-open-outline", {
      placement: "bottom",
      scene: "browse",
      zoom: false,
      actions: [{ do: "click", target: "files-folder-PROJECT/p-nord" }],
    }),
    inFolder("list", "files-list", "mdi-format-list-bulleted", { placement: "top", scene: "browse" }),
    // Explained, never pressed: it opens the operating system's own picker, which the frame has no
    // way to close, and the upload itself is a write.
    inFolder("upload", "files-upload", "mdi-upload-outline", { placement: "bottom", scene: "browse" }),
    inFolder("owner", "files-open-owner", "mdi-open-in-new", { placement: "bottom", scene: "browse" }),
    inFolder("due-column", DUE_CELL, "mdi-calendar-check", { placement: "left", scene: "browse" }),

    // ── one file ─────────────────────────────────────────────────────────────
    // The window whole before anything in it is zoomed into.
    inFolder("details", "modal-dialog", "mdi-file-document-outline", {
      placement: "bottom",
      scene: "browse",
      actions: [{ do: "click", target: DETAILS_ROW }],
    }),
    inFolder("details-meta", ["files-detail-tags", "files-detail-document-date", "files-detail-due"], "mdi-tag-outline", {
      placement: "right",
      scene: "browse",
    }),

    // ── Where to find this again ─────────────────────────────────────────────
    inFolder("again", "view-help-trigger", "mdi-help-circle-outline", { placement: "bottom" }),

    // ── Zusammenhänge ──────────────────────────────────────────────────────────
    //
    // Two pages, three steps. Not a tour of either of them: what matters here is only where the
    // documents in the browser come from, what the date in the "Gültig bis" column sets off, and
    // what decides which places the browser has at all.
    onProject("project-files", "project-files", "mdi-briefcase-upload-outline", {
      placement: "top",
      scene: "project-files",
      zoom: false,
      actions: [{ do: "openTab", target: "project-tab-files" }],
    }),
    onSettings("notify-documents", "settings-notify-documents", "mdi-bell-alert-outline", { placement: "right" }),
    // The whole page in shot while the register is pressed: zoomed onto the tab, the list of roles
    // that appears underneath would be out of the picture.
    onRights("rights", "settings-role-list", "mdi-shield-account-outline", {
      placement: "right",
      scene: "settings-rights",
      zoom: false,
      actions: [{ do: "openTab", target: "tab-rights" }],
    }),
  ],
}
